import { create } from "zustand";

import { setApiUser } from "@/api/client";
import type { components } from "@/api/schema.gen";

type MarketView = components["schemas"]["MarketView"];

export type ViewKey =
  | "markets"
  | "trending"
  | "sports"
  | "politics"
  | "crypto"
  | "culture"
  | "science"
  | "portfolio"
  | "activity"
  | "profile"
  | "market-detail";

export type CategoryFilter =
  | "all"
  | "crypto"
  | "politics"
  | "sports"
  | "tech"
  | "science"
  | "culture"
  | "macro";

interface TradePanelState {
  market: MarketView;
  side: "buy" | "sell";
  outcomeId: string;
}

interface ToastState {
  kind: "success" | "error" | "info";
  message: string;
}

interface State {
  // Auth
  userId: string | null;
  userEmail: string | null;
  setUser: (userId: string | null, email: string | null) => void;

  // Layout
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /** Drawer della nav su schermi stretti. Sotto `lg` la sidebar esce dal flusso
   *  e si apre sopra il contenuto: su 375px occupava piu' di meta' schermo. */
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;

  view: ViewKey;
  setView: (v: ViewKey) => void;

  // Discovery
  category: CategoryFilter;
  setCategory: (c: CategoryFilter) => void;
  layout: "grid" | "list";
  setLayout: (l: "grid" | "list") => void;

  // Detail + trade
  detailSlug: string | null;
  openDetail: (slug: string | null) => void;
  tradePanel: TradePanelState | null;
  openTrade: (
    market: MarketView,
    outcomeId: string,
    side: "buy" | "sell",
  ) => void;
  closeTrade: () => void;

  // Modals
  walletOpen: boolean;
  setWalletOpen: (v: boolean) => void;
  searchOpen: boolean;
  setSearchOpen: (v: boolean) => void;
  depositOpen: boolean;
  setDepositOpen: (v: boolean) => void;
  withdrawOpen: boolean;
  setWithdrawOpen: (v: boolean) => void;
  notifOpen: boolean;
  setNotifOpen: (v: boolean) => void;

  // Toast
  toast: ToastState | null;
  showToast: (t: ToastState) => void;
}

export const useStore = create<State>((set) => ({
  userId: localStorage.getItem("quantica.userId"),
  userEmail: localStorage.getItem("quantica.userEmail"),
  setUser: (userId, email) => {
    if (userId) {
      localStorage.setItem("quantica.userId", userId);
      if (email) localStorage.setItem("quantica.userEmail", email);
    } else {
      localStorage.removeItem("quantica.userId");
      localStorage.removeItem("quantica.userEmail");
    }
    setApiUser(userId);
    set({ userId, userEmail: email });
  },

  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  mobileNavOpen: false,
  setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),

  view: "markets",
  // Cambiare vista chiude il drawer: su mobile resterebbe aperto sopra la
  // pagina appena scelta.
  setView: (view) => set({ view, mobileNavOpen: false }),

  category: "all",
  setCategory: (category) => set({ category }),
  layout: "grid",
  setLayout: (layout) => set({ layout }),

  detailSlug: null,
  openDetail: (slug) =>
    set(
      slug
        ? { detailSlug: slug, view: "market-detail" }
        : { detailSlug: null, view: "markets" },
    ),

  tradePanel: null,
  openTrade: (market, outcomeId, side) =>
    set({ tradePanel: { market, outcomeId, side } }),
  closeTrade: () => set({ tradePanel: null }),

  walletOpen: false,
  setWalletOpen: (v) => set({ walletOpen: v }),
  searchOpen: false,
  setSearchOpen: (v) => set({ searchOpen: v }),
  depositOpen: false,
  setDepositOpen: (v) => set({ depositOpen: v }),
  withdrawOpen: false,
  setWithdrawOpen: (v) => set({ withdrawOpen: v }),
  notifOpen: false,
  setNotifOpen: (v) => set({ notifOpen: v }),

  toast: null,
  showToast: (toast) => {
    set({ toast });
    setTimeout(() => set({ toast: null }), 3200);
  },
}));

// Hydrate API client at startup.
setApiUser(useStore.getState().userId);
